import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Upload, Trash2 } from 'lucide-react';

interface FileUploadProps {
  file: File | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  uploadProgress?: number;
  fileTitle?: string;
  placeholder?: string;
}

const ALLOWED_FILE_TYPES = ["jpeg", "png", "jpg", "pptx", "docx", "doc", "pdf", "ppt", "xlsx", "xls", "xlsm"];

export default function ProcessMapUploadForm({
  file,
  onFileSelect,
  onFileRemove,
  fileTitle,
  placeholder
}: FileUploadProps) {
  const [filePreview, setFilePreview] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();

      // Generate preview for images or PDFs
      if (file.type.startsWith('image/')) {
        reader.onload = () => setFilePreview(reader.result as string);
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }

      return () => {
        if (file.type === 'application/pdf') {
          URL.revokeObjectURL(filePreview || '');
        }
      };
    }
  }, [file]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && ALLOWED_FILE_TYPES.includes(droppedFile.name.split('.').pop()?.toLowerCase() || '')) {
      onFileSelect(droppedFile);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && ALLOWED_FILE_TYPES.includes(selectedFile.name.split('.').pop()?.toLowerCase() || '')) {
      onFileSelect(selectedFile);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Show Preview Only When File is Selected */}
      {file && filePreview && (
        <Box
          sx={{
            width: '100%',
            height: 240,
            bgcolor: '#F8F9FA',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {file.type.startsWith('image/') ? (
            <img
              src={filePreview}
              alt={file.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : file.type === 'application/pdf' ? (
            <iframe
              src={filePreview}
              title="PDF preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', padding: 2 }}>
              Preview not available for this file type. Please download to view.
            </Typography>
          )}
        </Box>
      )}

      {/* Show Upload Box Only If No File is Selected */}
      {!file && (
        <Box
          sx={{
            border: '1.5px dashed #000',
            borderRadius: 3,
            pt: 5,
            pb: 5,
            pl: 4,
            pr: 4,
            textAlign: 'center',
            cursor: 'pointer',
          }}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <input
            type="file"
            id="file-input"
            hidden
            accept=".jpg,.jpeg,.png,.pdf,.pptx,.docx,.doc,.ppt,.xlsx,.xls,.xlsm"
            onChange={handleFileInput}
          />
          <Upload size={24} className="mx-auto mb-2" />
          <Typography variant="body1" sx={{ mb: 1 }}>
            Drag & Drop or <span style={{ color: '#2563EB' }}>Choose file</span> to upload
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {fileTitle}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {placeholder ? ` (${placeholder})` : 'File types: JPEG, JPG, PNG, PDF, PPTX, DOCX, DOC, PPT, XLSX, XLS, XLSM'}
          </Typography>
        </Box>
      )}

      {/* Show File Name & Remove Button When File is Selected */}
      {file && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2">{file.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {Math.round(file.size / 1024)} KB
          </Typography>
          <IconButton size="small" onClick={onFileRemove}>
            <Trash2 size={16} color="#FF4D4F" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}